import { List, Skeleton, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import {
  RouteContext,
  SidebarThemeContext,
  useFeatureFlags,
} from 'src/context';
import { MobileCompanySwitcher } from 'src/features/companies/CompanySwitcher';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import { Drawer } from 'src/features/drawer/Drawer';
import { CompanySwitcher as SidebarCompanySwitcher } from 'src/features/sidebar/CompanySwitcher';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { RootState } from 'src/store';

import { SettingsSidebarBackToggle } from './SettingsSidebarBackToggle';
import { SidebarLogoContainer } from './SidebarLogoContainer';
import SidebarNav from './SidebarNav';
import { useSidebarTheme } from './useSidebarTheme';

const SidebarSkeletonItems = [
  { id: 'crm' },
  { id: 'messaging' },
  { id: 'files' },
];

interface SidebarProps {
  openMobile: boolean;
  onMobileClose: () => void;
}

const SidebarContainer: React.FC<SidebarProps> = ({
  openMobile,
  onMobileClose,
}) => {
  const { EnableNewSidebar } = useFeatureFlags();
  const { sidebarTheme } = useSidebarTheme();
  const isSettingsLoaded = useSelector(
    (state: RootState) => state.settings.loaded,
  );
  const isExtensionsItemsLoaded = useSelector(
    (state: RootState) => state.dashboard.itemsLoaded,
  );
  const { isLoading: isLoadingInstalls } = useGetInstallsQuery();
  const { visibility: companySwitcherVisibility } =
    useCompanySwitcherVisibility();
  const { pathname } = useContext(RouteContext);
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // show sidebar skeleton if app state is not ready
  // or settings are not loaded
  // or extensions items are not loaded yet
  // or RTK Query is still loading installs data
  const showSidebarSkeleton =
    !isSettingsLoaded || !isExtensionsItemsLoaded || isLoadingInstalls;
  const isSettingsPage = pathname.includes('settings');

  return (
    <SidebarThemeContext.Provider value={sidebarTheme}>
      <Drawer onClose={onMobileClose} open={openMobile}>
        <div className={`pt-4 px-2 ${EnableNewSidebar ? 'pb-3' : 'pb-7'}`}>
          {/* On main sidebar show portal logo */}
          {companySwitcherVisibility === 'mobile' ? (
            EnableNewSidebar ? (
              <SidebarCompanySwitcher />
            ) : (
              <MobileCompanySwitcher />
            )
          ) : (
            <SidebarLogoContainer isSettingsPage={isSettingsPage} />
          )}
          {/* On settings sidebar show back to portal button only on desktop */}
          {isSettingsPage && !isSmScreen && <SettingsSidebarBackToggle />}
        </div>
        <List
          disablePadding
          className={`flex flex-col pb-1 h-full${EnableNewSidebar ? ' min-h-0' : ''}`}
        >
          {showSidebarSkeleton ? (
            SidebarSkeletonItems.map(({ id }) => (
              <div key={id} className="py-1 px-2">
                <Skeleton variant="rectangular" width="100%" height={28} />
              </div>
            ))
          ) : (
            <>
              <SidebarNav />
            </>
          )}
        </List>
      </Drawer>
    </SidebarThemeContext.Provider>
  );
};

export default SidebarContainer;
